import { useState } from 'react'
import toast from 'react-hot-toast'
import {CheckCircle2} from 'lucide-react'
import { services } from '@/data/services'

interface FormState {
  name: string
  email: string
  phone: string
  company: string
  service: string
  message: string
}

const initialState: FormState = {
  name: '',
  email: '',
  phone: '',
  company: '',
  service: '',
  message: '',
}

interface ConsultationFormProps {
  variant?: 'full' | 'compact'
}

function ConsultationForm({ variant = 'full' }: ConsultationFormProps) {
  const [form, setForm] = useState<FormState>(initialState)
  const [errors, setErrors] = useState<Partial<FormState>>({})
  const [submitted, setSubmitted] = useState(false)

  const validate = (): boolean => {
    const nextErrors: Partial<FormState> = {}
    if (!form.name.trim())
      nextErrors.name = 'Informe seu nome completo.'
    if (!form.email.trim() || !form.email.includes('@'))
      nextErrors.email = 'Informe um e-mail válido.'
    if (!form.phone.trim())
      nextErrors.phone = 'Informe um telefone de contato.'
    if (!form.message.trim())
      nextErrors.message = 'Descreva brevemente sua necessidade.'
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      toast.error('Por favor, corrija os campos indicados.')
      return
    }
    setSubmitted(true)
    toast.success('Solicitação enviada com sucesso!')
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-cyan-100 bg-cyan-50 p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-cyan-600" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-slate-900">Solicitação recebida!</h3>
        <p className="max-w-sm text-sm text-slate-600">
          Obrigado pelo contato, {form.name.split(' ')[0]}. Nossa equipe entrará em contato em até 24 horas úteis para agendar sua consultoria.
        </p>
        <button
          type="button"
          onClick={() => { setSubmitted(false); setForm(initialState) }}
          className="mt-2 text-sm font-semibold text-cyan-700 hover:underline"
        >
          Enviar outra solicitação
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">Nome completo *</label>
          <input
            id="name"
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            aria-invalid={Boolean(errors.name)}
            className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none focus:ring-1 ${errors.name ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
          />
          {errors.name ? <p className="mt-1 text-xs text-red-600">{errors.name}</p> : null}
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">E-mail corporativo *</label>
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            aria-invalid={Boolean(errors.email)}
            className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none focus:ring-1 ${errors.email ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
          />
          {errors.email ? <p className="mt-1 text-xs text-red-600">{errors.email}</p> : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-slate-700">Telefone *</label>
          <input
            id="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange('phone')}
            aria-invalid={Boolean(errors.phone)}
            className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none focus:ring-1 ${errors.phone ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
          />
          {errors.phone ? <p className="mt-1 text-xs text-red-600">{errors.phone}</p> : null}
        </div>
        <div>
          <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-slate-700">Empresa</label>
          <input
            id="company"
            type="text"
            value={form.company}
            onChange={handleChange('company')}
            className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          />
        </div>
      </div>

      {variant === 'full' ? (
        <div>
          <label htmlFor="service" className="mb-1.5 block text-sm font-medium text-slate-700">Solução de interesse</label>
          <select
            id="service"
            value={form.service}
            onChange={handleChange('service')}
            className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          >
            <option value="">Selecione uma solução</option>
            {services.map(s => (
              <option key={s.id} value={s.title}>{s.title}</option>
            ))}
          </select>
        </div>
      ) : null}

      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-slate-700">Descreva sua necessidade *</label>
        <textarea
          id="message"
          rows={4}
          value={form.message}
          onChange={handleChange('message')}
          aria-invalid={Boolean(errors.message)}
          className={`w-full resize-none rounded-lg border px-3.5 py-2.5 text-sm outline-none focus:ring-1 ${errors.message ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:border-cyan-500 focus:ring-cyan-500'}`}
        />
        {errors.message ? <p className="mt-1 text-xs text-red-600">{errors.message}</p> : null}
      </div>

      <button
        type="submit"
        className="w-full rounded-lg bg-cyan-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-700"
      >
        Solicitar Consultoria
      </button>
      <p className="text-center text-xs text-slate-400">
        Ao enviar, você concorda com nossa <a href="/privacidade" className="underline hover:text-cyan-600">Política de Privacidade</a>.
      </p>
    </form>
  )
}

export default ConsultationForm
